import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { array, bool, object } from 'prop-types';
import useStyles from 'hooks/useStyles';

import { ChevronIcon } from 'components/Icon';
import CourseContext from 'routes/course/components/CourseContext';
import { getLessonEventTrackData } from 'routes/course/hooks/useLessonEventTrack';

import Material from '../Material';
import styles from './MaterialList.scss';

const DEFAULT_MATERIAL_COUNT = 2;
const COURSE_MATERIAL_ID = 'course';

function MaterialList({
  elementRef,
  materials,
  lessonWithMaterials,
  showAllAtOnce,
  currentLesson,
  isLessonPage,
}) {
  useStyles(styles);
  const { t } = useTranslation(['course']);
  const { userPurchased } = useContext(CourseContext);

  const [showAllMaterials, setShowAllMaterials] = useState(showAllAtOnce);
  const toggleShowAllMaterials = () => setShowAllMaterials(prev => !prev);
  const materialsWithCourse = [
    {
      title: t('videoplayer.allMaterial'),
      materials,
      id: COURSE_MATERIAL_ID,
    },
    ...lessonWithMaterials,
  ];

  const partialMaterials = materialsWithCourse.slice(0, DEFAULT_MATERIAL_COUNT);

  const renderMaterialList = item => {
    const {
      title,
      materials: lessonMaterials,
      lessonOrder,
      id,
      preview,
    } = item;
    const isDisable = !preview && !userPurchased;
    const showTitle = !(showAllAtOnce && id === COURSE_MATERIAL_ID);

    if (lessonMaterials.length === 0) {
      return null;
    }

    const lessonEventTrackData =
      id === COURSE_MATERIAL_ID
        ? {}
        : getLessonEventTrackData({ lesson: item });

    return (
      <li key={id} className={styles.material}>
        {showTitle && (
          <h3 className={styles.title}>
            {lessonOrder &&
              t('videoplayer.lessonMaterial', { count: lessonOrder })}{' '}
            {title}
          </h3>
        )}
        <Material
          isDisable={isDisable}
          lessonEventTrackData={lessonEventTrackData}
          materials={lessonMaterials}
          isLessonPage={isLessonPage}
        />
      </li>
    );
  };

  const showMoreButton =
    !showAllMaterials &&
    materialsWithCourse.length > DEFAULT_MATERIAL_COUNT &&
    !currentLesson;

  return (
    <section ref={elementRef}>
      {!showAllAtOnce && <h2>{t('videoplayer.label.material')}</h2>}
      {currentLesson ? (
        <Material
          isDisable={!currentLesson.preview && !currentLesson.userPurchased}
          lessonEventTrackData={getLessonEventTrackData({
            lesson: currentLesson,
          })}
          materials={currentLesson.materials}
          isLessonPage={isLessonPage}
        />
      ) : (
        <ul className={styles.root}>
          {showAllMaterials
            ? materialsWithCourse.map(renderMaterialList)
            : partialMaterials.map(renderMaterialList)}
        </ul>
      )}

      {showMoreButton && (
        <div className={styles.showMore} onClick={toggleShowAllMaterials}>
          {t('videoplayer.more')}
          <ChevronIcon className={styles.icon} width={24} height={24} />
        </div>
      )}
    </section>
  );
}

MaterialList.propTypes = {
  materials: array.isRequired,
  lessonWithMaterials: array.isRequired,
  elementRef: object,
  showAllAtOnce: bool,
  currentLesson: object,
  isLessonPage: bool,
};

MaterialList.defaultProps = {
  elementRef: null,
  showAllAtOnce: false,
  currentLesson: undefined,
  isLessonPage: false,
};

export default MaterialList;
